import React from 'react'
import PropTypes from 'prop-types'
import {Text, Flex, Link, useMultiStyleConfig} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import {getFutureStockDate} from '../../utils/order-availability-utils'
import {remCalc} from '../../theme/_utils'
import {useCustomPreferenceWithFallback} from '../../hooks/use-custom-preference'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

/**
 * PreorderBackorderLabel component
 * Displays labels for products available for preorder or backorder
 */
const PreorderBackorderLabel = ({product, locale, variant = 'preorder'}) => {
    const style = useMultiStyleConfig('ProductView')
    const backorderRedirectPDP = useCustomPreferenceWithFallback('backorderRedirectPDP')
    const {locale: siteLocale} = useMultiSite()
    const localeId = siteLocale?.alias || locale
    const localizedBackorderLink = backorderRedirectPDP
        ? `/${localeId}${
              backorderRedirectPDP.startsWith('/')
                  ? backorderRedirectPDP
                  : `/${backorderRedirectPDP}`
          }`
        : null
    if (variant === 'preorder') {
        const preorderDate = getFutureStockDate(product, locale)

        return (
            <Flex {...style.preorderLabel}>
                <Text {...style.preorderLabelTitle}>
                    <FormattedMessage
                        id="product_view.preorder.title"
                        defaultMessage="Available for Preorder"
                    />
                </Text>
                {preorderDate ? (
                    <Text
                        {...style.preorderLabelDate}
                        fontWeight="medium"
                        color="foreground.neutral.default"
                        textStyle="body-small"
                    >
                        <FormattedMessage
                            id="order_summary.message.available_from"
                            defaultMessage="This item will be available from {date}."
                            values={{
                                date: (
                                    <Text as="span" fontWeight="bold">
                                        {preorderDate}
                                    </Text>
                                ),
                            }}
                        />
                    </Text>
                ) : (
                    <Text
                        {...style.preorderLabelDate}
                        fontWeight="medium"
                        color="foreground.neutral.default"
                        textStyle="body-small"
                    >
                        <FormattedMessage
                            id="product_view.preorder.available_soon"
                            defaultMessage="This item will be available soon."
                        />
                    </Text>
                )}
            </Flex>
        )
    }

    if (variant === 'backorder') {
        const backorderDate = getFutureStockDate(product, locale)

        return (
            <Flex {...style.preorderLabel} gap={remCalc(4)}>
                {backorderDate ? (
                    <Text {...style.backorderLabelTitle}>
                        <FormattedMessage
                            id="product_view.backorder.available_from_date"
                            defaultMessage="Available from {date}"
                            values={{date: backorderDate}}
                        />
                    </Text>
                ) : (
                    <Text {...style.backorderLabelTitle}>
                        <FormattedMessage
                            id="product_view.backorder.available_soon"
                            defaultMessage="Available soon"
                        />
                    </Text>
                )}
                <Text
                    {...style.backorderLabelDate}
                    fontWeight="medium"
                    color="foreground.neutral.default"
                    textStyle="body-small"
                >
                    <FormattedMessage
                        id="product_view.backorder.order_today"
                        defaultMessage="You can order it today."
                    />
                </Text>
                <Link
                    {...style.backorderDiscoverLink}
                    href={localizedBackorderLink}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FormattedMessage
                        id="product_view.backorder.discover_more"
                        defaultMessage="Discover more"
                    />
                </Link>
            </Flex>
        )
    }

    return null
}

PreorderBackorderLabel.propTypes = {
    product: PropTypes.object.isRequired,
    locale: PropTypes.string,
    variant: PropTypes.oneOf(['preorder', 'backorder']).isRequired,
}

PreorderBackorderLabel.defaultProps = {
    locale: 'en-GB',
}

export default PreorderBackorderLabel
